<template>
    <div class="venue-detail-wrapper">
        <div class="detail-container" v-if="venue">
            <!-- 모바일 전용 헤더 -->
            <div class="detail-header mobile-only">
                <button class="back-arrow-btn" @click="$router.push('/venue')" title="목록으로">
                    ←
                </button>
                <button class="share-btn" @click="showShareModal = true" title="공유하기">
                    <img src="@/assets/images/ios_share.png" alt="공유" class="share-icon" />
                </button>
            </div>

            <div class="detail-content">
                <div class="detail-poster-container">
                    <img v-if="venue.imageUrl" :src="venue.imageUrl" :alt="venue.name" class="detail-poster" />
                    <div v-else class="no-image-placeholder">
                        <span class="venue-icon">🏢</span>
                    </div>
                </div>
                <div class="detail-info">
                    <div class="detail-title">
                        <div class="title-left">
                            <h1>{{ venue.name }}</h1>
                            <a v-if="venue.mapLink" :href="venue.mapLink" target="_blank" class="detail-more-link"
                                @click="trackMapClick">
                                지도보기
                            </a>
                        </div>
                        <button class="share-btn desktop-only" @click="showShareModal = true" title="공유하기">
                            <img src="@/assets/images/ios_share.png" alt="공유" class="share-icon" />
                        </button>
                    </div>

                    <div class="info-table">
                        <div class="info-row" v-if="venue.address">
                            <div class="info-label">주소</div>
                            <div class="info-value">{{ venue.address }}</div>
                        </div>
                        <div class="info-row" v-if="venue.contact">
                            <div class="info-label">연락처</div>
                            <div class="info-value">{{ venue.contact }}</div>
                        </div>
                        <div class="info-row">
                            <div class="info-label">등록 공연</div>
                            <div class="info-value">
                                {{ venue.liveCount || 0 }}개
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 광고 섹션 (다른 Detail 화면과 동일하게 탭 위에) -->
            <div v-if="adBanners.length > 0" class="ad-section-wrapper">
                <AdSection :adBanners="adBanners" @ad-click="goToAdLink" />
            </div>

            <!-- 탭 섹션 -->
            <div class="tabs-section">
                <div class="tab-navigation">
                    <button :class="['tab-button', { active: activeTab === 'performances' }]"
                        @click="activeTab = 'performances'">
                        공연목록
                    </button>
                    <!-- <button :class="['tab-button', { active: activeTab === 'info' }]" @click="activeTab = 'info'">
                        공연장 정보
                    </button> -->
                </div>

                <div class="tab-content">
                    <!-- 공연목록 탭 -->
                    <div v-if="activeTab === 'performances'" class="tab-panel">
                        <div v-if="performancesLoading" class="empty-content">
                            <div class="spinner"></div>
                            <p>공연 정보를 불러오는 중...</p>
                        </div>
                        <div v-else-if="performances && performances.length > 0" class="performances-grid">
                            <PerformanceCard v-for="performance in performances" :key="performance.id"
                                :performance="performance" :show-like-button="true"
                                :is-liked="isPerformanceLiked(performance.id)" @click="goToPerformance"
                                @like-toggle="togglePerformanceLike" />
                        </div>
                        <div v-else class="empty-content">등록된 공연이 없습니다.</div>
                    </div>

                    <!-- 공연장 정보 탭 -->
                    <div v-if="activeTab === 'info'" class="tab-panel">
                        <div class="venue-info-content">
                            <div class="info-item" v-if="venue.name">
                                <h3 class="info-item-label">공연장명</h3>
                                <p class="info-item-value">{{ venue.name }}</p>
                            </div>
                            <div class="info-item" v-if="venue.address">
                                <h3 class="info-item-label">주소</h3>
                                <p class="info-item-value">{{ venue.address }}</p>
                            </div>
                            <div class="info-item" v-if="venue.contact">
                                <h3 class="info-item-label">연락처</h3>
                                <p class="info-item-value">{{ venue.contact }}</p>
                            </div>
                            <div class="info-item" v-if="venue.mapLink">
                                <h3 class="info-item-label">지도</h3>
                                <p class="info-item-value">
                                    <a :href="venue.mapLink" target="_blank" class="map-link">지도 열기 →</a>
                                </p>
                            </div>
                            <div class="info-item" v-if="venue.liveCount">
                                <h3 class="info-item-label">총 공연횟수</h3>
                                <p class="info-item-value">{{ venue.liveCount }}회</p>
                            </div>
                            <div class="info-item" v-if="!venue.address && !venue.contact && !venue.mapLink">
                                <div class="empty-content">등록된 상세 정보가 없습니다.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="loading">
            <div class="spinner"></div>
            <p>공연장 정보를 불러오는 중...</p>
        </div>

        <!-- 공유 방법 선택 모달 -->
        <share-options-modal :visible="showShareModal" @close="showShareModal = false"
            @select="handleShareMethod"></share-options-modal>
    </div>
</template>

<script>
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import PerformanceCard from "@/components/PerformanceCard.vue";
import AdSection from "@/components/AdSection.vue";
import { trackActivity, ACTIVITY_TYPES, trackShare } from "@/utils/activityTracker";
import { toggleLiveLike } from "@/utils/write/user";
import { incrementBannerClickCount } from "@/utils/write/banner";
import { setPageMeta, shareViaKakao, shareViaTwitter } from "@/utils/pageMeta";
import ShareOptionsModal from "@/components/modal/ShareOptionsModal.vue";

export default {
    name: "VenueDetail",
    components: {
        PerformanceCard,
        AdSection,
        ShareOptionsModal,
    },
    data() {
        return {
            venue: null,
            performances: [],
            loading: true,
            performancesLoading: false,
            error: null,
            userStore: useUserStore(),
            activeTab: "performances",
            userLiveLikes: [], // 사용자가 찜한 공연 목록
            adBanners: [],
            showShareModal: false,
        };
    },
    computed: {
        posterBackgroundStyle() {
            return this.venue?.imageUrl
                ? `background-image: url(${this.venue.imageUrl})`
                : "";
        },
    },
    async mounted() {
        await this.fetchVenueDetail();
        await this.loadUserLiveLikes();
        this.fetchAdBanners();
    },
    watch: {
        "$route.params.name": {
            handler(newName) {
                if (newName) {
                    this.fetchVenueDetail();
                }
            },
        },
    },
    methods: {
        trackMapClick() {
            trackActivity(ACTIVITY_TYPES.MAP_VIEW_CLICK, {
                target: "venue",
                targetId: this.venue?.name,
                metadata: { mapLink: this.venue?.mapLink },
            });
        },
        async fetchVenueDetail() {
            try {
                this.loading = true;
                const venueName = decodeURIComponent(this.$route.params.name);

                // 공연장 정보 조회
                const venueDoc = await getDoc(doc(firestore, "places", venueName));

                if (venueDoc.exists()) {
                    const venueData = venueDoc.data();
                    this.venue = {
                        name: venueDoc.id,
                        ...venueData,
                        liveCount: 0,
                        imageUrl: null,
                    };

                    // 이미지 URL 백그라운드 로드 (페이지 표시 지연 방지)
                    if (venueData.img) {
                        const imageRef = storageRef(storage, `venue/${venueData.img}`);
                        getDownloadURL(imageRef)
                            .then((url) => {
                                this.venue.imageUrl = url;
                                setPageMeta({
                                    title: this.venue.name,
                                    description: this.venue.address || undefined,
                                    image: url,
                                });
                            })
                            .catch((error) => console.warn("공연장 이미지 로드 실패:", error));
                    }
                    setPageMeta({
                        title: this.venue.name,
                        description: this.venue.address || undefined,
                        image: this.venue.imageUrl || undefined,
                    });
                } else {
                    // places 컬렉션에 없으면 lives에서 검색하여 공연장 정보 구성
                    this.venue = {
                        name: venueName,
                        address: null,
                        contact: null,
                        mapLink: null,
                        liveCount: 0,
                        imageUrl: null,
                    };
                    setPageMeta({
                        title: this.venue.name,
                        description: undefined,
                        image: undefined,
                    });
                }

                this.loading = false;

                // 공연 목록은 백그라운드에서 로드 (페이지 먼저 표시)
                this.fetchPerformances(venueName);
            } catch (error) {
                console.error("공연장 정보 조회 실패:", error);
                this.error = "공연장 정보를 불러오는데 실패했습니다.";
                this.loading = false;
            }
        },

        async fetchPerformances(venueName) {
            try {
                this.performancesLoading = true;

                const livesQuery = query(
                    collection(firestore, "lives"),
                    where("place", "==", venueName),
                    where("approved", "==", true)
                );

                const livesSnapshot = await getDocs(livesQuery);

                // 포스터 URL 병렬 로드 (순차 대신 동시 요청)
                const performancePromises = livesSnapshot.docs.map(async (liveDoc) => {
                    const liveData = liveDoc.data();
                    let imgUrl = null;
                    if (liveData.img) {
                        try {
                            const imageRef = storageRef(storage, `poster/${liveData.img}`);
                            imgUrl = await getDownloadURL(imageRef);
                        } catch (error) {
                            console.warn("포스터 이미지 로드 실패:", error);
                        }
                    }
                    return {
                        id: liveDoc.id,
                        title: liveData.title || "제목 없음",
                        date: liveData.date || null,
                        artist: liveData.artist || null,
                        img: imgUrl,
                    };
                });

                const performances = await Promise.all(performancePromises);

                // 날짜순 정렬 (최신순)
                performances.sort((a, b) => {
                    if (!a.date) return 1;
                    if (!b.date) return -1;
                    return new Date(b.date) - new Date(a.date);
                });

                this.performances = performances;
                this.venue.liveCount = performances.length;

                this.performancesLoading = false;
            } catch (error) {
                console.error("공연 목록 조회 실패:", error);
                this.performancesLoading = false;
            }
        },

        goToPerformance(id) {
            this.$router.push(`/live/${id}`);
        },

        async handleShareMethod(method) {
            this.showShareModal = false;
            const url = window.location.href;
            const title = `${this.venue.name} - 링꾹라이브`;

            if (method === "kakao") {
                await shareViaKakao();
                trackShare("venue", this.venue?.id || this.$route.params.id, "kakao");
                return;
            }
            if (method === "twitter") {
                shareViaTwitter();
                trackShare("venue", this.venue?.id || this.$route.params.id, "twitter");
                return;
            }
            if (method === "copy") {
                trackShare("venue", this.venue?.id || this.$route.params.id, "copy");
                this.copyToClipboard(url);
                return;
            }
            if (method === "native" && navigator.share) {
                try {
                    await navigator.share({ title, url });
                    trackShare("venue", this.venue?.id || this.$route.params.id, "native");
                } catch (error) {
                    if (error.name !== "AbortError") {
                        trackShare("venue", this.venue?.id || this.$route.params.id, "copy_fallback");
                        this.copyToClipboard(url);
                    }
                }
            }
        },

        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.$alert("링크가 복사되었습니다.");
            }).catch(() => {
                this.$alert("링크 복사에 실패했습니다.");
            });
        },

        // 사용자 찜 목록 로드
        async loadUserLiveLikes() {
            if (!this.userStore.isLogin) {
                this.userLiveLikes = [];
                return;
            }

            try {
                const { getUserLikes } = await import("@/utils/read/user");
                const userLikes = await getUserLikes(this.userStore.email);
                this.userLiveLikes = userLikes.liveLikes || [];
            } catch (error) {
                console.error("사용자 찜 데이터 로드 오류:", error);
                this.userLiveLikes = [];
            }
        },

        // 공연 찜 여부 확인
        isPerformanceLiked(performanceId) {
            return this.userLiveLikes.includes(performanceId);
        },

        async fetchAdBanners() {
            try {
                const bannersQuery = query(
                    collection(firestore, "banners"),
                    where("active", "==", true),
                    where("type", "==", "detail")
                );
                const bannersSnapshot = await getDocs(bannersQuery);

                const customBannerData = await Promise.all(
                    bannersSnapshot.docs.map(async (docSnap) => {
                        const data = docSnap.data();
                        if (data.startDateTime || data.endDateTime) {
                            const now = new Date();
                            if (data.startDateTime && new Date(data.startDateTime) > now) return null;
                            if (data.endDateTime && new Date(data.endDateTime) < now) return null;
                        }
                        let bannerImgUrl = data.img;
                        if (!bannerImgUrl) return null;
                        if (!bannerImgUrl.startsWith("http")) {
                            try {
                                const imageRef = storageRef(storage, `banner/${data.img}`);
                                bannerImgUrl = await getDownloadURL(imageRef);
                            } catch {
                                return null;
                            }
                        }
                        return {
                            id: docSnap.id,
                            source: "custom",
                            title: data.title,
                            img: bannerImgUrl,
                            link: data.link || "",
                            priority: data.priority || 0,
                        };
                    })
                );

                const allBanners = customBannerData.filter(Boolean);
                allBanners.sort((a, b) => (b.priority || 0) - (a.priority || 0));
                this.adBanners = allBanners;
            } catch (error) {
                console.error("광고 배너 로드 실패:", error);
                this.adBanners = [];
            }
        },

        async goToAdLink(ad) {
            await this.incrementAdClickCount(ad);
            if (ad.source === "custom" && ad.link) {
                window.open(ad.link, "_blank");
            }
        },

        async incrementAdClickCount(ad) {
            try {
                await incrementBannerClickCount(ad);
            } catch (error) {
                console.error("광고 클릭 횟수 증가 오류:", error);
            }
        },

        // 공연 찜 토글
        async togglePerformanceLike(performanceId) {
            if (!this.userStore.isLogin) {
                this.userStore.showLoginPopup('liveLike');
                return;
            }

            try {
                const isCurrentlyLiked = this.isPerformanceLiked(performanceId);

                const newLikedState = await toggleLiveLike(
                    performanceId,
                    this.userStore.email,
                    isCurrentlyLiked
                );

                // 로컬 상태 업데이트
                if (newLikedState) {
                    this.userLiveLikes.push(performanceId);
                } else {
                    this.userLiveLikes = this.userLiveLikes.filter(
                        (id) => id !== performanceId
                    );
                }
            } catch (error) {
                console.error("공연 찜 오류:", error);
                this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
            }
        },
    },
};
</script>

<style scoped>
.venue-detail-wrapper {
    width: 100%;
}

.detail-container {
    width: 90%;
    margin: auto;
    background: #fff;
    padding: 0px 16px;
    margin-bottom: 40px;
}

.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 16px 0px;
}

.mobile-only {
    display: flex;
}

.desktop-only {
    display: none !important;
}

.back-arrow-btn {
    background: transparent;
    border: none;
    font-size: 20px;
    cursor: pointer;
    padding: 6px 10px;
    color: #333;
    transition: opacity 0.2s ease;
}

.back-arrow-btn:hover {
    opacity: 0.7;
}

.share-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;
}

.share-btn:hover {
    opacity: 0.7;
}

.share-icon {
    width: 22px;
    height: 22px;
    object-fit: contain;
}

/* 상세 콘텐츠 레이아웃 */
.detail-content {
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    gap: 18px;
    align-items: stretch;
}

.detail-poster-container {
    position: relative;
    min-width: 0px;
    width: 100%;
    padding-bottom: 56.25%;
    /* 16:9 비율 */
    margin-bottom: 10px;
    overflow: hidden;
    border-radius: 12px;
    background: #f5f5f5;
}

.detail-poster {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.no-image-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.venue-icon {
    font-size: 100px;
    opacity: 0.3;
}

/* 상세 정보 */
.detail-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    margin-top: 0px;
    margin-bottom: 24px;
    gap: 16px;
    min-width: 0;
    width: 100%;
    padding: 0;
}

.detail-title {
    width: 100%;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.title-left {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.detail-title h1 {
    font-size: 1.25rem;
    color: #333;
    margin: 0;
    word-break: keep-all;
    font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
    letter-spacing: -0.5px;
    line-height: 1.4;
}

.detail-more-link {
    font-size: 12px;
    color: #2196f3;
    cursor: pointer;
    text-decoration: none;
    font-family: "NanumSquareRoundR", sans-serif;
    white-space: nowrap;
}

.detail-more-link:hover {
    text-decoration: underline;
}

/* 정보 테이블 */
.info-table {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    width: 100%;
}

.info-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 16px;
}

.info-label {
    width: 70px;
    min-width: 70px;
    color: #888;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    font-family: "NanumSquareRoundEB", sans-serif;
    letter-spacing: -0.3px;
    margin-top: 2px;
}

.info-value {
    color: #333;
    font-size: 13px;
    font-weight: 400;
    text-align: left;
    word-break: keep-all;
    font-family: "Pretendard", sans-serif;
    line-height: 1.5;
    letter-spacing: -0.2px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}


.ad-section-wrapper {
    margin-top: 16px;
    margin-bottom: 8px;
    width: 100%;
    min-width: 0;
}

/* 탭 섹션 */
.tabs-section {
    margin-top: 24px;
    width: 100%;
    background: #fafbfc;
    overflow: hidden;
}

.tab-navigation {
    display: flex;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
}

.tab-button {
    flex: 1;
    padding: 12px 10px;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: "NanumSquareRoundR", sans-serif;
    border-bottom: 3px solid transparent;
    border-radius: 0px;
}

.tab-button:hover {
    color: #333;
    background: #f5f5f5;
}

.tab-button.active {
    color: #2196f3;
    background: #f8fafe;
    border-bottom-color: #2196f3;
}

.tab-content {
    padding: 20px 12px 16px 12px;
    min-height: 200px;
}

.tab-panel {
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #999;
    font-size: 16px;
    padding: 60px 20px;
    font-family: "NanumSquareRoundR", sans-serif;
}

.empty-content .spinner {
    width: 60px;
    height: 60px;
    border: 6px solid #f3f3f3;
    border-top: 6px solid #66bdff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
}

.empty-content p {
    margin: 0;
    color: #999;
    font-size: 16px;
    font-family: "NanumSquareRoundR", sans-serif;
}

/* 공연 그리드 */
.performances-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    max-width: 1000px;
    margin: 0 auto;
}

/* 공연장 정보 탭 */
.venue-info-content {
    max-width: 600px;
    margin: 0 auto;
}

.info-item {
    margin-bottom: 16px;
    padding: 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-item-label {
    font-size: 14px;
    font-weight: bold;
    color: #333;
    margin-bottom: 8px;
    font-family: "NanumSquareRoundB", sans-serif;
}

.info-item-value {
    font-size: 14px;
    color: #666;
    margin: 0;
    font-family: "NanumSquareRoundR", sans-serif;
}

.map-link {
    color: #2196f3;
    text-decoration: none;
}

.map-link:hover {
    text-decoration: underline;
}

/* 로딩 */
.loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
}

.spinner {
    width: 60px;
    height: 60px;
    border: 6px solid #f3f3f3;
    border-top: 6px solid #66bdff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading p {
    font-size: 14px;
    color: #666;
    font-family: "NanumSquareRoundR", sans-serif;
}

/* 반응형 - 모바일 */
@media screen and (max-width: 480px) {
    .spinner {
        width: 50px;
        height: 50px;
        border: 5px solid #f3f3f3;
        border-top: 5px solid #66bdff;
    }

    .loading p {
        font-size: 13px;
    }

    .empty-content .spinner {
        width: 50px;
        height: 50px;
        border: 5px solid #f3f3f3;
        border-top: 5px solid #66bdff;
        margin-bottom: 15px;
    }

    .empty-content p {
        font-size: 13px;
    }

    .detail-container {
        padding: 0px 10px;
    }

    .detail-title h1 {
        font-size: 1.1rem;
    }

    .detail-more-link {
        font-size: 11px;
    }

    .back-arrow-btn {
        font-size: 18px;
        padding: 4px 8px;
    }

    .share-icon {
        width: 20px;
        height: 20px;
    }

    .detail-content {
        gap: 16px;
    }

    .detail-poster-container {
        border-radius: 10px;
    }

    .detail-info {
        gap: 8px;
    }

    .info-table {
        gap: 6px;
    }

    .info-label {
        width: 55px;
        min-width: 55px;
        font-size: 11px;
    }

    .info-value {
        font-size: 12px;
    }

    .tabs-section {
        margin-top: 20px;
    }

    .tab-button {
        padding: 10px 6px;
        font-size: 12px;
    }

    .tab-content {
        padding: 12px 6px 10px 6px;
    }

    .performances-grid {
        grid-template-columns: repeat(1, 1fr);
        gap: 12px;
    }

    .performance-poster {
        height: 140px;
    }

    .no-poster-placeholder {
        height: 120px;
        font-size: 10px;
    }

    .performance-content {
        padding: 8px;
        gap: 6px;
    }

    .performance-title {
        font-size: 12px;
        margin-bottom: 4px;
    }

    .performance-date {
        font-size: 11px;
    }

    .performance-artist {
        font-size: 11px;
    }

    .info-item {
        padding: 12px;
        margin-bottom: 12px;
    }

    .info-item-label {
        font-size: 13px;
        margin-bottom: 6px;
    }

    .info-item-value {
        font-size: 13px;
    }
}
</style>
